import clsx from 'clsx';
import React from 'react';

import Bookmark from '@components/Bookmark';
import { TextButton } from '@components/Button/variants';
import BylineWithHeadshot from '@components/BylineWithHeadshot';
import ClientRenderedContent from '@components/ClientRenderedContent';
import IconLink from '@components/Images/Rebranding/IconLink';
import ListenButton from '@components/ListenButton';
import { useAppContext } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import useNativeAppVersionCheck from '@hooks/useNativeAppVersionCheck';
import type { Author } from '@interfaces/authors';
import { ClickTarget, ContentType, click } from '@tracking';

import type { BriefingSource } from './index';

interface BriefingDetailProps {
  authors: Author[];
  canViewFullBriefing: boolean;
  id: number;
  isAudioActive: boolean;
  publishedAt: string;
  setIsAudioActive: (isAudioActive: boolean) => void;
  source: BriefingSource;
}

const BriefingDetails = ({
  authors,
  canViewFullBriefing,
  isAudioActive,
  id,
  publishedAt,
  setIsAudioActive,
  source,
}: BriefingDetailProps) => {
  const { trackEvent } = useEventTracking();
  const { initialCurrentUser } = useAppContext();
  const { isApp3_0OrGreater, isNativeApp } = useNativeAppVersionCheck();
  const showListenButton =
    initialCurrentUser?.is_subscriber && (!isNativeApp || (isNativeApp && isApp3_0OrGreater));

  return (
    <div
      className={clsx(
        'briefing-detail grid w-full grid-cols-[1fr_auto] items-start gap-x-16',
        "[grid-template-areas:'byline_byline'_'divider_divider'_'time_actions'_'source_source']",
        'md:flex md:max-w-[350px] md:flex-col md:items-stretch',
        { 'pointer-events-none opacity-60': !canViewFullBriefing }
      )}
    >
      <div className="[grid-area:byline]">
        <BylineWithHeadshot
          authors={authors}
          authorClassName="author-name-body"
          className="max-w-sm"
        />
      </div>

      <div className="flex gap-x-16 justify-self-end [grid-area:actions] md:mt-24 md:self-end">
        {showListenButton && (
          <ListenButton
            contentType={ContentType.Briefing}
            isAudioActive={isAudioActive}
            setIsAudioActive={setIsAudioActive}
          />
        )}
        <Bookmark contentId={id} contentType={ContentType.Briefing} />
      </div>

      <div className="mb-10 mt-12 w-full border-0 border-b border-solid border-grayscale-gray [grid-area:divider] md:mb-0 md:mt-0 md:pt-24" />

      <div
        className={clsx(
          'contents text-grayscale-gray md:mt-8 md:flex md:flex-wrap md:items-center md:justify-between md:gap-24 lg:items-start',
          { 'md:opacity-50': !canViewFullBriefing }
        )}
      >
        <ClientRenderedContent>
          <time
            className="block font-suisse-intl-mono text-overline-sm font-normal text-grayscale-gray [grid-area:time] sm:text-overline-md lg:text-overline-sm xl:text-overline-md"
            dateTime={publishedAt}
          >
            {publishedAt}
          </time>
        </ClientRenderedContent>
        <div className="mt-8 flex items-center [grid-area:source] md:mr-16 md:mt-0">
          <IconLink className="h-16 w-16 text-grayscale-gray" />
          <span className="px-8 font-suisse-intl-mono text-overline-sm text-grayscale-gray">
            Source:
          </span>
          <TextButton
            as="link"
            href={source.url}
            className={clsx(
              '!w-3/5 !justify-start font-suisse-intl text-icon-sm text-grayscale-gray no-underline md:!justify-center',
              { 'opacity-50': !canViewFullBriefing }
            )}
            onClickCapture={() =>
              trackEvent(
                click({
                  clickLink: source.url,
                  clickTarget: ClickTarget.BriefingSource,
                  clickText: source.name,
                })
              )
            }
          >
            {source.name}
          </TextButton>
        </div>
      </div>
    </div>
  );
};

export default BriefingDetails;
